import { useCallback, useEffect, useRef, useState } from 'react';
import { TTS_VOICES, WORDS_PER_SECOND, type NarrationStatus, type ProjectSettings, type Scene } from '@gc/shared';
import { ApiError, apiRequest } from '../../api/client.js';

export type PanelNarracionProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly settings: ProjectSettings;
  readonly scenes: readonly Scene[];
  onCambio(): void;
};

type VozEstado = { readonly jobId: string; readonly sceneId: string };

const palabras = (t: string) => t.trim().split(/\s+/).filter(Boolean).length;

/**
 * Narración (opcional). El director de texto propone una locución por escena
 * que cabe en su duración; aquí se edita, se guarda en las escenas y se genera
 * el audio con la voz elegida. El audio queda en la biblioteca y lo usa el
 * montaje; también se puede sustituir por una locución propia en «Escenas».
 */
export function PanelNarracion({ projectId, revision, status, settings, scenes, onCambio }: PanelNarracionProps): React.JSX.Element {
  // Con una API anterior los campos de voz pueden faltar: se asumen los defectos.
  const vozGuardada = settings.voiceName ?? 'alloy';
  const estiloGuardado = settings.voiceStyle ?? '';
  const velocidadGuardada = settings.voiceSpeed ?? 1;
  const [activa, setActiva] = useState(settings.voiceEnabled);
  const [voz, setVoz] = useState(vozGuardada);
  const [estilo, setEstilo] = useState(estiloGuardado);
  const [velocidad, setVelocidad] = useState(velocidadGuardada);
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [notas, setNotas] = useState('');
  const [propuesta, setPropuesta] = useState<NarrationStatus | null>(null);
  const [vocesEnCurso, setVocesEnCurso] = useState<VozEstado[]>([]);
  const [muestra, setMuestra] = useState<{ jobId: string; assetId: string | null; clave: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const avisar = useRef(onCambio);
  avisar.current = onCambio;

  // Lo guardado manda; lo editado localmente se conserva hasta guardar.
  useEffect(() => {
    setActiva(settings.voiceEnabled);
    setVoz(vozGuardada);
    setEstilo(estiloGuardado);
    setVelocidad(velocidadGuardada);
  }, [settings.voiceEnabled, vozGuardada, estiloGuardado, velocidadGuardada]);
  useEffect(() => {
    setTextos((actual) => {
      const siguiente: Record<string, string> = {};
      for (const escena of scenes) siguiente[escena.id] = actual[escena.id] ?? escena.text ?? '';
      return siguiente;
    });
  }, [scenes]);

  const recargarPropuesta = useCallback(async () => {
    try {
      setPropuesta(await apiRequest<NarrationStatus>(`/projects/${projectId}/narration`));
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo consultar la narración.');
    }
  }, [projectId]);
  useEffect(() => { void recargarPropuesta(); }, [recargarPropuesta]);

  const proponiendo = propuesta?.status === 'PENDING' || propuesta?.status === 'RUNNING';
  useEffect(() => {
    if (!proponiendo) return;
    const intervalo = setInterval(() => void recargarPropuesta(), 3_000);
    return () => clearInterval(intervalo);
  }, [proponiendo, recargarPropuesta]);

  // Seguimiento de las locuciones encoladas: al terminar, la página recarga.
  useEffect(() => {
    if (vocesEnCurso.length === 0) return;
    const intervalo = setInterval(() => {
      void (async () => {
        const restantes: VozEstado[] = [];
        for (const v of vocesEnCurso) {
          try {
            const job = await apiRequest<{ status: string; error: { message?: string } | null }>(`/jobs/${v.jobId}`);
            if (job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED') {
              if (job.status === 'FAILED') setError(`Una locución falló: ${job.error?.message ?? 'sin detalle'}`);
            } else restantes.push(v);
          } catch { restantes.push(v); }
        }
        if (restantes.length !== vocesEnCurso.length) {
          setVocesEnCurso(restantes);
          avisar.current();
        }
      })();
    }, 3_000);
    return () => clearInterval(intervalo);
  }, [vocesEnCurso]);

  // Muestra de voz: se pide (idempotente) y se consulta hasta tener el audio.
  const claveMuestra = `${voz}|${velocidad}|${estilo.trim()}`;
  useEffect(() => {
    if (muestra === null || muestra.assetId !== null || muestra.clave !== claveMuestra) return;
    const intervalo = setInterval(() => {
      void apiRequest<{ job: { id: string; status: string; error: { message?: string } | null }; assetId: string | null }>(`/projects/${projectId}/narration/voice-sample`, {
        method: 'POST',
        body: { voiceName: voz, voiceStyle: estilo, voiceSpeed: velocidad },
      })
        .then((r) => {
          if (r.assetId !== null) setMuestra({ jobId: r.job.id, assetId: r.assetId, clave: claveMuestra });
          else if (r.job.status === 'FAILED') { setError(`La muestra falló: ${r.job.error?.message ?? 'sin detalle'}`); setMuestra(null); }
        })
        .catch(() => undefined);
    }, 2_500);
    return () => clearInterval(intervalo);
  }, [muestra, claveMuestra, projectId, voz, estilo, velocidad]);

  async function escucharMuestra(): Promise<void> {
    await accion(async () => {
      const r = await apiRequest<{ job: { id: string }; assetId: string | null }>(`/projects/${projectId}/narration/voice-sample`, {
        method: 'POST',
        body: { voiceName: voz, voiceStyle: estilo, voiceSpeed: velocidad },
      });
      setMuestra({ jobId: r.job.id, assetId: r.assetId, clave: claveMuestra });
    }, false);
  }

  async function accion(fn: () => Promise<unknown>, recargarPagina = true): Promise<void> {
    setOcupado(true); setError(null);
    try { await fn(); if (recargarPagina) avisar.current(); }
    catch (causa) { setError(causa instanceof ApiError ? causa.message : 'La operación falló.'); }
    finally { setOcupado(false); }
  }

  const bloqueado = ocupado || status === 'GENERATING' || status === 'RENDERING';
  const haySinGuardar =
    activa !== settings.voiceEnabled || voz !== vozGuardada || estilo !== estiloGuardado || velocidad !== velocidadGuardada ||
    scenes.some((e) => (textos[e.id] ?? '') !== (e.text ?? ''));
  const conTexto = scenes.filter((e) => (e.text ?? '').trim() !== '');
  const sinVoz = conTexto.filter((e) => e.voiceAssetId === null);

  function aplicarPropuesta(): void {
    if (!propuesta?.script) return;
    const porPosicion = new Map(propuesta.script.scenes.map((s) => [s.position, s.text]));
    setTextos(Object.fromEntries(scenes.map((e) => [e.id, porPosicion.get(e.position) ?? textos[e.id] ?? ''])));
    if (estilo.trim() === '') setEstilo(propuesta.script.deliveryStyle);
    setActiva(true);
  }

  async function guardar(): Promise<void> {
    await accion(() =>
      apiRequest(`/projects/${projectId}/narration`, {
        method: 'PUT',
        body: { expectedRevision: revision, voiceEnabled: activa, voiceName: voz, voiceStyle: estilo, voiceSpeed: velocidad, scenes: scenes.map((e) => ({ sceneId: e.id, text: textos[e.id] ?? null })) },
      }),
    );
  }

  async function generarAudio(onlyMissing: boolean): Promise<void> {
    await accion(async () => {
      const r = await apiRequest<{ jobs: { id: string; sceneId: string | null }[]; skipped: number }>(`/projects/${projectId}/narration/voice`, {
        method: 'POST',
        body: { expectedRevision: revision, onlyMissing },
      });
      setVocesEnCurso(r.jobs.map((j) => ({ jobId: j.id, sceneId: j.sceneId ?? '' })));
    }, false);
  }

  return (
    <section aria-labelledby="titulo-narracion" style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}>
      <h2 id="titulo-narracion" style={{ marginTop: 0 }}>Narración</h2>
      <p style={{ marginTop: 0 }}>
        <small>
          Opcional. Pide una propuesta de locución por escena, edítala, guárdala y genera el audio con la voz que elijas.
          El audio queda en tu biblioteca y entra en el montaje; si la locución es más larga que su escena, la revisión te avisará.
        </small>
      </p>

      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        <input type="checkbox" checked={activa} disabled={bloqueado} onChange={(e) => setActiva(e.target.checked)} /> Incluir narración en el vídeo
      </label>

      <fieldset style={{ margin: '0 0 1rem' }}>
        <legend>Voz</legend>
        <label>
          Voz{' '}
          <select value={voz} disabled={bloqueado} onChange={(e) => setVoz(e.target.value)}>
            {Object.entries(TTS_VOICES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>{' '}
        <label>
          Velocidad{' '}
          <input type="range" min={0.5} max={1.5} step={0.05} value={velocidad} disabled={bloqueado} onChange={(e) => setVelocidad(Number(e.target.value))} style={{ verticalAlign: 'middle', width: '8rem' }} /> {velocidad.toFixed(2)}×
        </label>
        <p style={{ margin: '0.5rem 0 0' }}>
          <label htmlFor="estilo-voz">Estilo de entrega (tono, ritmo; no se lee en voz alta)</label><br />
          <input id="estilo-voz" value={estilo} maxLength={300} disabled={bloqueado} onChange={(e) => setEstilo(e.target.value)} placeholder="Cálida, pausada, cercana" style={{ width: '100%', maxWidth: '40rem' }} />
        </p>
        <p style={{ margin: '0.5rem 0 0', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" disabled={bloqueado} onClick={() => void escucharMuestra()}>
            Escuchar muestra de esta voz
          </button>
          {muestra !== null && muestra.clave === claveMuestra && muestra.assetId === null && <span role="status"><small>Generando la muestra…</small></span>}
          {muestra !== null && muestra.clave === claveMuestra && muestra.assetId !== null && (
            <audio src={`/api/v1/assets/${muestra.assetId}/content`} controls autoPlay preload="auto" style={{ maxWidth: '24rem' }} />
          )}
          <small>Una frase fija con la voz, velocidad y estilo actuales; la misma combinación no se vuelve a generar.</small>
        </p>
      </fieldset>

      <fieldset style={{ margin: '0 0 1rem' }}>
        <legend>Propuesta del guionista</legend>
        <p style={{ margin: '0 0 0.5rem' }}>
          <label htmlFor="notas-narracion">Indicaciones (público, qué debe decir, qué evitar)</label><br />
          <input id="notas-narracion" value={notas} disabled={bloqueado || proponiendo} onChange={(e) => setNotas(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
        </p>
        <p style={{ margin: 0 }}>
          <button type="button" disabled={bloqueado || proponiendo || scenes.length === 0} onClick={() => void accion(() => apiRequest(`/projects/${projectId}/narration/propose`, { method: 'POST', body: { notes: notas } }).then(recargarPropuesta), false)}>
            {proponiendo ? 'Proponiendo…' : propuesta?.script ? 'Pedir otra propuesta' : 'Proponer narración'}
          </button>{' '}
          {propuesta?.script && (
            <button type="button" disabled={bloqueado} onClick={aplicarPropuesta}>Aplicar propuesta a los textos</button>
          )}
          {scenes.length === 0 && <small> Genera primero la estructura.</small>}
        </p>
        {propuesta?.status === 'FAILED' && <p role="alert">La propuesta falló: {propuesta.error ?? 'sin detalle'}.</p>}
        {propuesta?.script && (
          <p style={{ margin: '0.5rem 0 0' }}>
            <small>Propuesta del {new Date(propuesta.createdAt ?? '').toLocaleString('es-ES')} · entrega sugerida: «{propuesta.script.deliveryStyle}». Al aplicarla se rellenan los textos de abajo; nada se guarda hasta pulsar «Guardar».</small>
          </p>
        )}
      </fieldset>

      <h3>Texto por escena</h3>
      {scenes.length === 0 && <p><small>Sin escenas todavía.</small></p>}
      {scenes.map((escena) => {
        const texto = textos[escena.id] ?? '';
        const maximo = Math.max(3, Math.floor((escena.durationMs / 1000) * WORDS_PER_SECOND));
        const n = palabras(texto);
        const propuesto = propuesta?.script?.scenes.find((s) => s.position === escena.position)?.text;
        return (
          <div key={escena.id} style={{ border: '1px solid var(--borde-suave)', borderRadius: '6px', padding: '0.5rem 0.75rem', margin: '0.5rem 0' }}>
            <p style={{ margin: '0 0 0.25rem' }}>
              <strong>{escena.position + 1}. {escena.title}</strong>{' '}
              <small>· {Math.round(escena.durationMs / 1000)} s · {n}/{maximo} palabras{n > maximo ? ' (probablemente no cabe)' : ''}</small>
            </p>
            <textarea
              aria-label={`Narración de la escena ${escena.position + 1}`}
              rows={3}
              value={texto}
              disabled={bloqueado}
              onChange={(e) => setTextos({ ...textos, [escena.id]: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Sin narración en esta escena"
            />
            {propuesto !== undefined && propuesto !== texto && (
              <p style={{ margin: '0.25rem 0 0' }}>
                <small>Propuesta: «{propuesto}»</small>{' '}
                <button type="button" disabled={bloqueado} onClick={() => setTextos({ ...textos, [escena.id]: propuesto })}>Usar</button>
              </p>
            )}
            {escena.voiceAssetId !== null && (
              <p style={{ margin: '0.25rem 0 0' }}>
                <audio src={`/api/v1/assets/${escena.voiceAssetId}/content`} controls preload="none" style={{ width: '100%', maxWidth: '28rem' }} />
                {(escena.text ?? '') !== texto && <><br /><small>El audio corresponde al texto guardado; cambia al regenerar.</small></>}
              </p>
            )}
            {vocesEnCurso.some((v) => v.sceneId === escena.id) && <p role="status" style={{ margin: '0.25rem 0 0' }}><small>Generando locución…</small></p>}
          </div>
        );
      })}

      <p style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
        <button type="button" disabled={bloqueado || !haySinGuardar} onClick={() => void guardar()}>
          Guardar textos y voz
        </button>
        <button type="button" disabled={bloqueado || haySinGuardar || sinVoz.length === 0} title={haySinGuardar ? 'Guarda primero los cambios' : undefined} onClick={() => void generarAudio(true)}>
          Generar audio de las escenas sin voz ({sinVoz.length})
        </button>
        <button type="button" disabled={bloqueado || haySinGuardar || conTexto.length === 0} title={haySinGuardar ? 'Guarda primero los cambios' : undefined} onClick={() => { if (window.confirm(`Se regenerarán ${conTexto.length} locuciones con la voz actual. ¿Continuar?`)) void generarAudio(false); }}>
          Regenerar todo el audio ({conTexto.length})
        </button>
      </p>
      <p><small>Generar audio llama al proveedor de voz y gasta crédito; las locuciones anteriores quedan en la biblioteca. Guardar sube la revisión del proyecto.</small></p>

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}
